import { useState } from "react";
import { use_store, get_media_url, MediaItem } from "../store";
import { useLocation } from "wouter";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLock } from "@fortawesome/pro-solid-svg-icons";
import Button from "./button";

export function StoryViewer() {
  const { account_analysis, is_elite, set_account_analysis, t_url, t } =
    use_store();
  const [visible_count, set_visible_count] = useState(10);
  const [, set_location] = useLocation();

  const stories = account_analysis?.media?.stories || [];
  const visible_stories = stories.slice(0, visible_count);
  const has_more = stories.length > visible_count;

  // Calculate time since last update
  const last_updated =
    account_analysis?.last_updated_media ||
    account_analysis?.last_updated_details;
  const hours_ago = last_updated
    ? Math.floor((Date.now() - last_updated * 1000) / (1000 * 60 * 60))
    : 24;

  const handleRevealMore = () => {
    if (is_elite) {
      set_visible_count((prev) => prev + 5);
    } else {
      set_account_analysis(undefined);
      window.scrollTo(0, 0);
      set_location(t_url("/try"));
    }
  };

  return (
    <div className="w-full flex flex-col items-center">
      {/* Header Section */}
      <div className="w-full max-w-[800px] flex flex-col items-center mb-[32px]">
        {/* Profile Picture */}
        <img
          src={get_media_url(account_analysis?.profile_pic_url)}
          alt={account_analysis?.username}
          className="w-[48px] h-[48px] rounded-full object-cover mb-[16px]"
        />

        {/* Updated time */}
        <p className="eyebrows text-primary mb-[16px]">
          {t("UPDATED {hours} HOURS AGO").replace("{hours}", String(hours_ago))}
        </p>

        {/* Title */}
        <h1 className="stitle font-bold mb-[24px]">
          {t("Stories from @{username}").replace(
            "{username}",
            account_analysis?.username || "",
          )}
        </h1>

        {/* Stats */}
        <div className="flex flex-col sm:flex-row gap-[16px] w-full max-w-[640px] eyebrows">
          <div className="py-[9px] px-[10px] flex flex-row gap-[8px] items-center justify-between flex-grow rounded-[4px] flex-1 min-w-[140px] bg-surface-secondary border-lines border-[1px]">
            <p>
              {t("{number} STORIES").replace(
                "{number}",
                String(stories.length),
              )}
            </p>
            <p>📸</p>
          </div>
          <div className="py-[9px] px-[10px] flex flex-row gap-[8px] items-center justify-between flex-grow rounded-[4px] flex-1 min-w-[140px] bg-surface-secondary border-lines border-[1px]">
            <p>{t("ANONYMOUS VIEWING")}</p>
            <p>👻</p>
          </div>
        </div>
      </div>

      {/* Stories Grid */}
      <div className="w-full max-w-[640px] grid grid-cols-1 sm:grid-cols-2 gap-[16px]">
        {visible_stories.map((story, index) => (
          <StoryCard key={story.platform_id} story={story} index={index} />
        ))}

        {is_elite && has_more && (
          <Button onClick={handleRevealMore}>
            <p className=" ">{t("Reveal More")}</p>
          </Button>
        )}
        {!is_elite && (
          <Button href={t_url("/try")}>
            <p className="">{t("Subscribe to Reveal All")}</p>
          </Button>
        )}
      </div>
    </div>
  );
}

interface StoryCardProps {
  story: MediaItem;
  index: number;
}

// @ts-ignore
function StoryCard({ story, index }: StoryCardProps) {
  const { is_elite, set_account_analysis, t_url, t } = use_store();
  const [, set_location] = useLocation();
  const [is_playing, set_is_playing] = useState(false);

  const handleRevealMore = () => {
    if (!is_elite) {
      set_account_analysis(undefined);
      window.scrollTo(0, 0);
      set_location(t_url("/try"));
    }
  };

  const handleMediaClick = () => {
    if (is_elite && story.content_url) {
      if (story.is_video) {
        set_is_playing(true);
      }
    } else if (!is_elite) {
      handleRevealMore();
    }
  };

  // Format time ago
  const hours_ago = Math.floor(
    (Date.now() - story.post_event_on * 1000) / (1000 * 60 * 60),
  );
  const time_display =
    hours_ago < 24 ? `${hours_ago}h ago` : `${Math.floor(hours_ago / 24)}d ago`;

  return (
    <div className="relative bg-surface rounded-[4px] overflow-hidden border-[1px] border-lines group">
      {/* Story Image/Video */}
      <div
        className={`aspect-[9/16] bg-surface-secondary relative cursor-pointer ${!is_elite && index > 1 ? "filter" : ""}`}
        onClick={handleMediaClick}
      >
        {story.is_video ? (
          <video
            src={get_media_url(story.content_url)}
            className="w-full h-full object-cover"
            controls={is_elite}
            autoPlay={is_playing}
            muted
            playsInline
            onPlay={() => set_is_playing(true)}
            onPause={() => set_is_playing(false)}
          />
        ) : (
          <img
            src={get_media_url(story.thumbnail_url)}
            alt={story.title || "Story"}
            className="w-full h-full object-cover"
          />
        )}

        {/* Play button overlay for videos */}
        {is_elite && story.is_video && !is_playing && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="bg-black/60 rounded-full p-[16px]">
              <svg
                className="w-[24px] h-[24px] text-white"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
              </svg>
            </div>
          </div>
        )}

        {/* Time indicator */}
        <div className="absolute top-[8px] left-[8px] bg-black/60 text-white px-[8px] py-[4px] rounded-[4px] eyebrows">
          {time_display}
        </div>

        {/* Lock overlay for non-elite users */}
        {!is_elite && index >= 0 && (
          <div
            className={`absolute inset-0 flex flex-col items-center justify-center bg-surface/30 backdrop-blur-[12px] hover:text-brand-2/20`}
            style={{
              backfaceVisibility: "hidden",
              perspective: 1000,
              transform: "translateZ(0)",
            }}
          >
            <div className="text-brand-2 rounded-full p-[20px] shadow-lg">
              <FontAwesomeIcon icon={faLock} className="stitle text-surface" />
            </div>
            <p className="eyebrows   mt-[12px] text-primary">
              {t("Subscribe to view")}
            </p>
          </div>
        )}
      </div>

      {/* Story Title (if available) */}
      {story.title && (
        <div className="p-[12px]">
          <p className="smallt text-primary truncate">{story.title}</p>
        </div>
      )}
    </div>
  );
}
